import { computed, ComputedRef, defineComponent, nextTick, onMounted, onUpdated, ref, Ref, watch } from "vue";
import debounce from "../../../../../skyward.ui.debounce";
import Loading from "../../../../core/loading";
import VSvg from "../../../../core/svg";
import { addScopedCallback, addScopedGlobalListener, OptionalVueProp } from "../../../../core/vueHelpers";
import { FetchDataBehavior, State } from "../../../core/store/types";
import { VueBrowseClasses } from "../../classes";
import { browseStore } from "../../store";
import { BrowseStoreState } from "../../store/types";

const maxFailedRequests = 5;
const scrollThreshold = 300;
const newRecordsLoadedThreshold = scrollThreshold * 2;

type InfiniteScrollFeatures =
{
    settings?:
    {
        infiniteScrollBodyIDOverride?: string | undefined;
        infiniteScrollBodyClassOverride?: string | undefined;
    };
};

const classes =
{
    busy: "infiniteScroll__busy"
};

const InfiniteScrollContainer = defineComponent(
{
    name: "infiniteScrollContainer",
    props:
    {
        customState: OptionalVueProp<State<{}, {}, InfiniteScrollFeatures>>(Object),
    },
    setup(props, { slots })
    {
        const state = props.customState ?? browseStore.injectBrowseState<BrowseStoreState<{}, {}>>();
        if (!state)
        {
            throw new Error("Unable to retrieve browse state");
        }

        const hasOverrideContainerSetting: ComputedRef<boolean> = computed(() => typeof(state.features?.settings?.infiniteScrollBodyIDOverride) === "string" || typeof(state.features?.settings?.infiniteScrollBodyClassOverride) === "string");
        const overrideInfiniteScrollContainerBodyID: ComputedRef<boolean> = computed(() => Boolean(state.features.settings?.infiniteScrollBodyIDOverride));
        const overrideInfiniteScrollContainerBodyClass: ComputedRef<boolean> = computed(() => Boolean(state.features.settings?.infiniteScrollBodyClassOverride));
        const infiniteScrollContainer: Ref<HTMLElement | undefined> = ref();
        const hasMoreRecords: Ref<boolean> = ref(true);
        const isLoadingContent: Ref<boolean> = ref(false);
        const isBusy = computed(() => isLoadingContent.value || !state.data.isInitiallyLoaded);
        const failedRequestCount: Ref<number> = ref(0);
        let scrollDiv: HTMLElement | null;
        const resizeHandler = () => { checkLoadContent(newRecordsLoadedThreshold); };
        const scrollHandler = () => { checkLoadContent(scrollThreshold) };
        let hasMadeInternalRequest = false;
        let checkLoadAfterDomUpdate = false;

        // It takes some time for content to render after an update.
        // This debounce function adds a delay to allow rendering of inner content to finish
        // before calling checkLoadContent.  Without this delay, the inner content may not
        // be fully rendered, causing scroll heights to be wrong.
        const debouncedLoadAfterDomUpdate = debounce(() =>
        {
            if (checkLoadAfterDomUpdate)
            {
                checkLoadAfterDomUpdate = false;
                checkLoadContent(newRecordsLoadedThreshold);
            }
        }, 0);

        // This is here to handle requests that were made by parent components on initial render
        addScopedCallback(state.data.onAfterFetch, () => 
        {
            // we only want to handle requests made on initial render here,
            // so bail out if we've made any requests ourselves
            if (hasMadeInternalRequest)
            {
                return;
            }
            // the initial data request came back with less records than a full page of recordrs => set hasMoreRecords to false
            if (state.data.length < state.features.pagingType.pageSize)
            {
                hasMoreRecords.value = false;
            }
            // the data request returned a full page of records, check for more records
            else if (state.data.length === state.features.pagingType.pageSize)
            {
                nextTick(() => checkLoadContent(newRecordsLoadedThreshold));
            }
        });

        watch(() => state.data.length, (newValue: number, oldValue: number) => 
        {
            // if the number of records ever decreases, reset these state variables to their initial states
            if (oldValue > newValue)
            {
                hasMadeInternalRequest = false;
                hasMoreRecords.value = true;
            }
        });

        onUpdated(debouncedLoadAfterDomUpdate);

        onMounted(() =>
        {
            if (hasOverrideContainerSetting.value)
            {
                if (overrideInfiniteScrollContainerBodyID.value) {
                    scrollDiv = document.getElementById(state.features.settings!.infiniteScrollBodyIDOverride!);
                }
                else if (overrideInfiniteScrollContainerBodyClass.value)
                {
                    if (props.customState)
                    {
                        scrollDiv = $(`.${state.features.settings!.infiniteScrollBodyClassOverride!}`).get(0)!;
                    }
                    else
                    {
                        scrollDiv = $(`#${(state as BrowseStoreState<{}, {}>).browseID}`).closest(`.${state.features.settings!.infiniteScrollBodyClassOverride!}`).get(0)!;
                    }
                }
                if (scrollDiv)
                {
                    scrollDiv.style.overflow = 'auto';
                }
            }
            else if (infiniteScrollContainer.value)
            {
                scrollDiv = infiniteScrollContainer.value as HTMLDivElement;
            }

            if (scrollDiv)
            {
                addScopedGlobalListener(scrollDiv, "scroll", scrollHandler);
            }

            addScopedGlobalListener(window, "resize", resizeHandler);

            // Call checkLoadContent if the data has already finished loading, 
            // since the onAfterFetch callback was not called.
            if (state.data.isInitiallyLoaded && !hasMadeInternalRequest)
            {
                checkLoadContent(newRecordsLoadedThreshold);
            }
        });

        return () => (
            <div
                ref={infiniteScrollContainer}
                class={{
                    [VueBrowseClasses.UnlockedBody]: true
                }}>
                {slots.default?.()}
                {hasMoreRecords.value ?
                    (failedRequestCount.value >= maxFailedRequests ?
                        <div class={classes.busy}><VSvg style="fill: #f90" name="Warning" showHoverEffect={false} /></div> :
                        <Loading class={classes.busy} style={`visibility:${isBusy.value ? "visible" : "hidden"}`} />) :
                    null}
            </div>
        );

        function checkLoadContent (threshold: number)
        {
            if (debouncedLoadAfterDomUpdate.isActive)
            {
                checkLoadAfterDomUpdate = true;
                return;
            }

            if (!hasMoreRecords.value ||
                isBusy.value ||
                !state.data.isLoaded ||
                failedRequestCount.value >= maxFailedRequests ||
                !scrollDiv)
            {
                return;
            }

            const distanceToBottom = scrollDiv.scrollHeight - scrollDiv.offsetHeight - scrollDiv.scrollTop;

            if (distanceToBottom < scrollThreshold)
            {
                isLoadingContent.value = true;
                hasMadeInternalRequest = true;
                state.data.fetch(FetchDataBehavior.Append)
                    .then((response) =>
                    {
                        const rowsReturned = response!.values?.length ?? 0;
                        isLoadingContent.value = false;
                        failedRequestCount.value = 0;

                        hasMoreRecords.value = (rowsReturned === state.features.pagingType.pageSize && rowsReturned !== 0);

                        // Check again, in case the fetched rows were not enough to fill the threshold space/
                        checkLoadContent(threshold);
                    }).catch(() =>
                    {
                        failedRequestCount.value++;
                        setTimeout(() =>
                        {
                            isLoadingContent.value = false;
                            checkLoadContent(threshold);
                        }, 1000);
                    });
            }
        }
    }
});

export default InfiniteScrollContainer;